import HeaderBackground from '@pedidosya/web-fenix/system/HeaderBackground';
import RowInformativeS from '@pedidosya/web-fenix/system/RowInformativeS';
import Typography from '@pedidosya/web-fenix/foundations/Typography';
import Accordion from '@pedidosya/web-fenix/system/Accordion';
import { InView } from 'react-intersection-observer';
import React, { useState } from 'react';
import Button from '@pedidosya/web-fenix/system/Button';
import BottomSheet from '@pedidosya/web-fenix/system/BottomSheet';
import styled from 'styled-components';
import TopAppBar from '@pedidosya/web-fenix/system/TopAppBar';
import {
  getBulletDescription,
  isVersionGreaterOrEqual,
} from '@/src/utils/utils';
import Calendar from '@pedidosya/web-fenix/icons/Calendar';
import Location from '@pedidosya/web-fenix/icons/Location';
import Coupon from '@pedidosya/web-fenix/icons/Coupon';
import Invoice from '@pedidosya/web-fenix/icons/Invoice';
import Refund from '@pedidosya/web-fenix/icons/Refound';
import CardIcon from '@pedidosya/web-fenix/icons/CardIcon';
import NoCigarette from '@pedidosya/web-fenix/icons/NoCigarette';
import Order from '@pedidosya/web-fenix/icons/Order';
import Information from '@pedidosya/web-fenix/icons/Information';
import parse from 'html-react-parser';
import { renderFenixIcon } from '@/src/utils/fenixIcons/fenixIcons';
import RowInformativeL from '@pedidosya/web-fenix/system/RowInformativeL';
import { useSelector } from 'react-redux';
import getAppVersion from '@/src/redux/selectors/shared/getAppVersion';
import DineIn from '@pedidosya/web-fenix/icons/DineIn';
import { useResizeObserver } from '@/src/hooks/useResizeObserver';
import { useForceRender } from '@/src/hooks/useForceRender';

const ChildrenBottomSheet = styled.div`
  padding: 16px 16px 48px 16px;
  h1 h2 h3 h4 h5 {
    padding-bottom: 16px;
  }
  ol {
    list-style-position: outside;
  }
  ul {
    list-style-position: outside;
    padding-left: 16px;
  }
  li {
    list-style-position: outside;
  }
`;

const Wrapper = styled.div`
  background-color: white !important;
`;

const RowInformativeStyle = styled(RowInformativeS)`
  > div {
    gap: 0 !important;
  }
`;

const DescriptionContainer = styled.div`
  padding-left: 40px;
  padding-bottom: 4px;
`;

const AccordionContainer = styled.div<{ $isCollapsed: boolean }>`
  background-color: white;
  transition: padding-bottom 0.3s ease;
  ${({ $isCollapsed }) => !$isCollapsed && `padding-bottom: 5%;`}
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
`;

const NoAccordionContainer = styled.div`
  background-color: white;
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledTopAppBar = styled(TopAppBar)<{
  isScroll?: boolean;
  isItemLanding?: boolean;
  isNewVersion?: boolean;
}>`
  ${({ platform }) =>
    platform === 'ios' &&
    `
      padding-top: env(safe-area-inset-top);
      height: 11vh !important;
    `}

  ${({ platform, isNewVersion, isItemLanding }) =>
    platform === 'android' &&
    isNewVersion &&
    isItemLanding &&
    `
      padding-top: 8% !important;
      height: 10vh !important;
    `}

    ${({ platform, isNewVersion, isItemLanding }) =>
    platform === 'android' &&
    isNewVersion &&
    !isItemLanding &&
    `
      padding-top: 12% !important;
      height: 10% !important;
    `}

  > div {
    ${({ platform, isScroll }) =>
      platform === 'ios' &&
      isScroll &&
      `
      padding-top: 5% !important;
      transition: padding-top 0.6s ease;
      `}
  }

  @media only screen and (device-width: 393px) and (device-height: 852px) and (-webkit-device-pixel-ratio: 3) {
    ${({ platform, isItemLanding }) =>
      platform === 'ios' &&
      `
    padding-top: ${isItemLanding ? '7%' : '8%'} !important;

    > div {
      ${({ isScroll }) =>
        isScroll &&
        `
          padding-top: 5% !important;
        `}
    }
  `}
  }
`;

const BulletIconWrapper = () => (
  <svg
    width="4"
    height="4"
    viewBox="0 0 6 6"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="3" cy="3" r="3" fill="#2d2d2d" />
  </svg>
);

const renderTopAppBar = (
  onGoBack,
  platform,
  isScroll,
  isItemLanding,
  appVersion,
) => (
  <Wrapper>
    <StyledTopAppBar
      onGoBack={onGoBack}
      variant="oncontent"
      left="Icon"
      right="Icon"
      position="fixed"
      platform={platform}
      isScroll={isScroll}
      isItemLanding={isItemLanding}
      isNewVersion={isVersionGreaterOrEqual(appVersion, '10.19.0.0')}
    />
  </Wrapper>
);

const ICON_MAP = {
  min_purchase: <Refund />,
  mov: <Refund />,
  refund: <Refund />,
  refound: <Refund />,
  calendar: <Calendar />,
  location: <Location />,
  tobacco: <NoCigarette />,
  no_cigarettes: <NoCigarette />,
  no_cigarette: <NoCigarette />,
  nocigarette: <NoCigarette />,
  voucher: <Coupon />,
  coupon: <Coupon />,
  invoice: <Invoice />,
  credit_card: <CardIcon />,
  card: <CardIcon />,
  order: <Order />,
  information: <Information />,
  dine_in: <DineIn />,
  dinein: <DineIn />,
  store: <DineIn />,
};

const getBulletIcon = (type) => {
  if (!type) return null;
  return ICON_MAP[type.toLowerCase()] || null;
};

export const HeaderBackgroundV3 = ({
  landing,
  onClickGoBack,
  fwfConfig,
  isScroll,
  isItemLanding,
  rootMargin,
  handleInViewChange,
}) => {
  const [showTyC, setShowTyC] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const landingIcon = renderFenixIcon(landing?.content_details?.icon);
  const appVersion = useSelector(getAppVersion);

  const onToggleAccordion = () => {
    setIsCollapsed(!isCollapsed);
  };

  const forceRender = useForceRender();
  const accordionChildWrapperRef = useResizeObserver(forceRender);

  const hasBullets = landing?.content_details?.bullets?.length > 0;
  const hasTyC =
    landing?.content_details?.terms_and_conditions?.content &&
    landing?.content_details?.terms_and_conditions?.title;
  const shouldShowAccordion = hasBullets || hasTyC;

  const renderContent = () => {
    if (!shouldShowAccordion) {
      return (
        <NoAccordionContainer>
          <TitleWrapper>
            {landingIcon && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {React.cloneElement(landingIcon as React.ReactElement, {
                  size: '24px',
                  color: '#2d2d2d',
                })}
              </div>
            )}
            <Typography
              token="font-headline-highcontrast-small"
              color="text-color-primary"
            >
              {landing?.content_details?.title}
            </Typography>
          </TitleWrapper>
          {landing?.content_details?.description && (
            <Typography token="text-base-medium" color="text-color-secondary">
              {landing?.content_details?.description}
            </Typography>
          )}
        </NoAccordionContainer>
      );
    }

    return (
      <AccordionContainer
        $isCollapsed={isCollapsed}
        onClick={onToggleAccordion}
      >
        <Accordion
          descriptionValue={
            landing?.content_details && landing?.content_details.description
          }
          titleValue={
            landing?.content_details && landing?.content_details.title
          }
          icon={landingIcon}
          collapsed={isCollapsed}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            ref={accordionChildWrapperRef}
          >
            <InView rootMargin={rootMargin} onChange={handleInViewChange}>
              {landing?.content_details?.bullets?.map((bullet) => {
                const icon = getBulletIcon(bullet?.type);

                if (!icon) {
                  const textLines = bullet.value.split('\n');
                  const descriptionText = getBulletDescription(bullet);

                  return (
                    <div
                      key={bullet.value}
                      style={{ paddingLeft: landingIcon ? 8 : 2 }}
                    >
                      {textLines.map((line) => (
                        <RowInformativeStyle
                          key={`${bullet.value}`}
                          primaryText={line}
                          icon={<BulletIconWrapper />}
                        />
                      ))}

                      {descriptionText && (
                        <DescriptionContainer>
                          <Typography
                            token="text-base-medium"
                            color="text-color-secondary"
                          >
                            {descriptionText}
                          </Typography>
                        </DescriptionContainer>
                      )}
                    </div>
                  );
                }

                return (
                  <RowInformativeL
                    key={bullet.value}
                    primaryLeftText={bullet.value}
                    secondaryLeftText={bullet.sub_value}
                  >
                    <RowInformativeL.LeftElementAsset>
                      {icon}
                    </RowInformativeL.LeftElementAsset>
                  </RowInformativeL>
                );
              })}
              {hasTyC && (
                <Button
                  label={landing?.content_details?.terms_and_conditions?.title}
                  hierarchy="tertiary"
                  size="small"
                  onClick={() => setShowTyC(!showTyC)}
                  onClose={() => setShowTyC(!showTyC)}
                  onClickOut={() => setShowTyC(!showTyC)}
                />
              )}
              <BottomSheet
                open={showTyC}
                variant="modal"
                scrollEnabled
                handler={false}
                title={landing?.content_details?.terms_and_conditions?.title}
                onClose={() => setShowTyC(!showTyC)}
                onClickOut={() => setShowTyC(!showTyC)}
              >
                <ChildrenBottomSheet>
                  {landing?.content_details?.terms_and_conditions?.content &&
                    parse(
                      landing?.content_details?.terms_and_conditions?.content,
                    )}
                </ChildrenBottomSheet>
              </BottomSheet>
            </InView>
          </div>
        </Accordion>
      </AccordionContainer>
    );
  };

  return (
    <HeaderBackground
      marginTopSlot1="86px" // VER CON UX SI ESTA VALOR ESTA BIEN
      topAppBar={renderTopAppBar(
        onClickGoBack,
        fwfConfig?.platform,
        isScroll,
        isItemLanding,
        appVersion,
      )}
      backgroundImage={landing?.header?.image}
      onGoBack={() => {}}
      onShare={() => {}}
      slot1={renderContent()}
    />
  );
};
